import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useBusiness } from '@/contexts/BusinessContext'
import { ChevronLeft, Search } from 'lucide-react'
import { useState, type ComponentType } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { cn } from '@/lib/utils'
import { isNavActive } from '@/lib/navigation'
import { settingsTabs } from '@/config/sidebar'

import { StoreAvatar } from './AccountMenu'

interface SettingsLink {
  to: string
  label: string
  icon?: ComponentType<{ className?: string }>
  /** Indented under another item (delivery methods and areas under shipping). */
  nested: boolean
  active: boolean
}

/** The settings group's pages, with `/settings` expanded into its tabs. Feature flags apply as in the main nav. */
function useSettingsLinks(): SettingsLink[] {
  const { getSidebarItems } = useFeatureFlags()
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const tab = params.get('tab') ?? settingsTabs[0].value
  const pages = getSidebarItems().find((item) => item.id === 'settings')?.children ?? []
  // The longest matching route wins, so /orders/methods lights up Delivery methods only.
  const current = pages
    .flatMap((p) => (p.to && isNavActive(pathname, p.to) ? [p.to] : []))
    .sort((a, b) => b.length - a.length)[0]

  return pages.flatMap((page): SettingsLink[] => {
    if (!page.to) return []
    if (page.to === '/settings') {
      return settingsTabs.map((t) => ({
        to: t.value === settingsTabs[0].value ? '/settings' : `/settings?tab=${t.value}`,
        label: t.label,
        icon: t.icon,
        nested: false,
        active: current === '/settings' && tab === t.value,
      }))
    }
    return [{ to: page.to, label: page.label ?? '', icon: page.icon, nested: !!page.parentTo, active: current === page.to }]
  })
}

interface SettingsNavProps {
  /** Where "‹ Settings" goes: the last page visited outside settings. */
  exitTo: string
  /** Called after a link is followed (closes the mobile sheet). */
  onNavigate?: () => void
}

/**
 * Shopify's settings column (§6.10), shown beside the icon rail on settings routes: a way back, a search that
 * filters the list, the store card and the settings pages.
 */
export const SettingsNav = ({ exitTo, onNavigate }: SettingsNavProps) => {
  const links = useSettingsLinks()
  const { profile } = useBusiness()
  const [query, setQuery] = useState('')
  const domain = profile?.custom_customer_domain || profile?.platform_customer_domain
  const q = query.trim().toLowerCase()
  const shown = q ? links.filter((l) => l.label.toLowerCase().includes(q)) : links

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-[52px] shrink-0 items-center px-3.5">
        <Link
          to={exitTo}
          onClick={onNavigate}
          className="flex h-7 items-center gap-1.5 rounded-md pl-1 pr-2 text-sm font-semibold text-white outline-none transition-colors hover:bg-sidebar-hover focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          Settings
        </Link>
      </div>

      <div className="space-y-3 px-3.5 pb-3">
        <label className="flex h-7 items-center gap-2 rounded-md bg-sidebar-input px-2 text-sm text-sidebar-foreground focus-within:ring-2 focus-within:ring-sidebar-ring">
          <Search className="size-4 shrink-0" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings"
            aria-label="Search settings"
            className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-sidebar-foreground/80"
          />
        </label>

        <div className="flex items-center gap-2.5 px-1">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{profile?.name}</p>
            {domain && <p className="truncate text-xs text-sidebar-muted">{domain}</p>}
          </div>
          <StoreAvatar className="size-8" />
        </div>
      </div>

      <nav className="no-scrollbar flex-1 overflow-y-auto px-3.5 pb-4" aria-label="Settings">
        {shown.length === 0 ? (
          <p className="px-2 py-1 text-sm text-sidebar-muted">No settings match “{query.trim()}”.</p>
        ) : (
          <ul className="space-y-0.5">
            {shown.map((l) => {
              const Icon = l.icon
              return (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={onNavigate}
                    aria-current={l.active ? 'page' : undefined}
                    className={cn(
                      'flex h-7 items-center gap-2 rounded-md px-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                      l.nested && !q && 'pl-8',
                      l.active
                        ? 'bg-gradient-to-r from-white/[0.13] to-white/[0.17] text-white [&>svg]:text-white'
                        : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white [&>svg]:text-sidebar-muted hover:[&>svg]:text-white'
                    )}
                  >
                    {Icon && !(l.nested && !q) && <Icon className="size-4 shrink-0" />}
                    <span className="truncate">{l.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </nav>
    </div>
  )
}
